import { Controller } from '@hotwired/stimulus';
import {
  feedbackCardRatingsIncomplete,
  patchDocumentUserState,
  hasAnswerCommentAvailable,
  noteSaveEpochsSettled,
  normalizeNodeUid,
} from '../../utils/case_experience/case_experience_in_rails';
import {
  findSubmitButtonContainerForElement,
  removeSpeechToTextToolbarFromContainer,
} from '../../utils/case_experience_in_rails';
import {
  applyDerivedPassiveSubmitInactive,
  notesTrimmedTextAllowsSubmit,
} from '../../utils/case_experience/passive_submit_eligibility';

export default class extends Controller {
  static targets = ['textarea', 'charCount', 'container', 'disclaimer', 'saveStatus'];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    boxLines: { type: Number, default: 5 },
    characterLimit: { type: Number, default: 1000 },
    charCount: Number,
    isSummaryStatement: Boolean,
    isFreeTextQuestion: Boolean,
    graded: Boolean,
    isAiSummaryFeedbackEnabled: Boolean,
    isGenericAiComponent: Boolean,
  };

  connect() {
    this.fromFeedbackCard = this.element.hasAttribute('data-feedback-card');
    this._noteEditEpoch = 0;
    this._noteLastSavedEpoch = 0;
    this._noteSaveInFlightCount = 0;
    this._activeSavePromise = null;
    this._flushPromise = null;
    this.saveTimeout = null;
    this._charCountAnnounceTimeout = null;
    this._saveStatusClearTimeout = null;

    this.setupTextareaPersistUi();
    this.bindSubmitButtonClick();
    this.setupInitialState();
    this.syncFeedbackCardTextareaEnabled();
  }

  /** Feedback textarea starts disabled in ERB; enable when no incomplete rating bars (incl. zero bars). */
  syncFeedbackCardTextareaEnabled() {
    if (!this.fromFeedbackCard || !this.hasTextareaTarget) return;

    const scope =
      this.element.closest('.feedback-card-content') ||
      this.element.closest('.doc-section.feedback_card');
    if (!scope) return;

    this.textareaTarget.disabled = feedbackCardRatingsIncomplete(scope);
  }

  setupTextareaPersistUi() {
    if (!this.hasTextareaTarget) {
      return;
    }

    if (!this._noteInputHandler) {
      this._noteInputHandler = () => {
        // Optional feedback comment must not gate submit (ratings do).
        if (this.fromFeedbackCard) {
          return;
        }
        if (this.element.querySelector('.ai-dynamic-submit')) {
          const button = this.getActiveSubmitButton();
          if (button) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
          }
          return;
        }
        const button = this.getActiveSubmitButton();
        if (button) {
          applyDerivedPassiveSubmitInactive({
            submitSlice: button.closest('.doc-button-slice'),
            application: this.application,
            buttonActionIdentifier: 'case-experience--button-action',
            enabled: false,
            submitButton: button,
          });
        }
      };
      this.textareaTarget.addEventListener('input', this._noteInputHandler);
    }

    if (!this._noteSavedHandler) {
      this._noteSavedHandler = (ev) => {
        if (this.fromFeedbackCard) {
          return;
        }
        const button = this.getActiveSubmitButton();
        if (button && noteSaveEpochsSettled(ev.detail)) {
          this.updateSubmitButtonState(true);
        }
      };
      this.textareaTarget.addEventListener('note:saved', this._noteSavedHandler);
    }

    this.textareaTarget.setAttribute('maxlength', this.characterLimitValue);
    this.updateCharCount();
  }

  getSiblingSubmitButton() {
    const container = findSubmitButtonContainerForElement(this.element);
    return container?.querySelector('button') ?? null;
  }

  getActiveSubmitButton() {
    const dynamic = this.element.querySelector('.ai-dynamic-submit');
    if (dynamic) return dynamic;
    return this.getSiblingSubmitButton();
  }

  bindSubmitButtonClick() {
    this._handleSubmitClickBound = this.handleSubmitClick.bind(this);
    const button = this.getSiblingSubmitButton();
    if (button) {
      this._submitButton = button;
      this._submitButton.addEventListener('click', this._handleSubmitClickBound);
    }
  }

  setupInitialState() {
    this.hideOrShowGradedFeedback();
    if (this.hasTextareaTarget) {
      if (this.isFreeTextQuestionValue && this.gradedValue && !this.fromFeedbackCard) {
        this.textareaTarget.disabled = true;
      }
      this.updateSubmitButtonState();
      this.syncSpeechToTextForTextareaState();
    }
  }

  syncSpeechToTextForTextareaState() {
    if (!this.hasTextareaTarget || !this.textareaTarget.disabled) return;
    removeSpeechToTextToolbarFromContainer(this.element);
  }

  hasVisibleAnswerCommentSection() {
    let sibling = this.element.parentElement?.firstElementChild;
    while (sibling) {
      if (
        sibling !== this.element &&
        sibling.classList &&
        sibling.classList.contains('doc-section') &&
        sibling.classList.contains('answer_comment') &&
        sibling.offsetParent !== null
      ) {
        return true;
      }
      sibling = sibling.nextElementSibling;
    }
    return false;
  }

  isStudentDisplayMode() {
    const docBody = this.element.closest('.doc-body');
    return !!docBody?.classList.contains('student-display-mode');
  }

  storeAnswerCommentHTML() {
    this._answerCommentSibling = null;
    this.answerCommentHtml = null;
  }

  hideOrShowGradedFeedback() {
    const gradedFeedback = this.element.querySelector('.graded-feedback');
    const hasAnswerComment = hasAnswerCommentAvailable(this.element, this.answerCommentHtml);
    if (!gradedFeedback || !this.gradedValue) {
      return;
    }
    if (hasAnswerComment) {
      gradedFeedback.innerHTML = this.getGradedFeedbackMessage();
      gradedFeedback.style.display = '';
    } else {
      gradedFeedback.innerHTML = '';
      gradedFeedback.style.display = 'none';
    }
  }

  getGradedFeedbackMessage() {
    if (this.isSummaryStatementValue) {
      return 'Your response will be recorded in your Student Report and may also be viewed by faculty.';
    } else {
      return 'The suggested answer is shown below.';
    }
  }

  updateCharCount() {
    if (!this.hasCharCountTarget || !this.hasTextareaTarget) return;
    const limit = this.characterLimitValue || this.textareaTarget.maxLength || 0;
    if (this.textareaTarget.value.length > limit) {
      this.textareaTarget.value = this.textareaTarget.value.slice(0, limit);
    }
    const count = this.textareaTarget.value.length;
    this.charCountValue = count;
    const formatted = this.isSummaryStatementValue || this.isGenericAiComponentValue
      ? `${count}/${limit}`
      : `${count} / ${limit}`;
    this.charCountTarget.innerHTML = formatted;

    const liveRegion = this.charCountLiveRegion();
    if (!liveRegion) return;
    if (document.activeElement !== this.textareaTarget) return;
    if (this._charCountAnnounceTimeout) clearTimeout(this._charCountAnnounceTimeout);
    this._charCountAnnounceTimeout = setTimeout(() => {
      if (!this.hasTextareaTarget || document.activeElement !== this.textareaTarget) return;
      const srMessage = `You have entered ${count} out of ${limit} characters allowed.`;
      liveRegion.setAttribute('aria-live', 'assertive');
      liveRegion.setAttribute('role', 'alert');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          liveRegion.textContent = srMessage;
        });
      });
      this._charCountAnnounceTimeout = null;
    }, 900);
  }

  charCountLiveRegion() {
    if (!this.hasTextareaTarget) return null;
    const escape =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape
        : (value) => String(value).replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
    return (
      this.element.querySelector(`#${escape(this.textareaTarget.id)}_counter_live`) ||
      this.element.querySelector(`#char-count-live-region-${this.nodeUidValue}`)
    );
  }

  disconnect() {
    const hasUnsavedEdits = this._noteEditEpoch > this._noteLastSavedEpoch;
    if (this.hasTextareaTarget) {
      if (this._noteInputHandler) {
        this.textareaTarget.removeEventListener('input', this._noteInputHandler);
        this._noteInputHandler = null;
      }
      if (this._noteSavedHandler) {
        this.textareaTarget.removeEventListener('note:saved', this._noteSavedHandler);
        this._noteSavedHandler = null;
      }
    }
    if (this._submitButton && this._handleSubmitClickBound) {
      this._submitButton.removeEventListener('click', this._handleSubmitClickBound);
    }
    this._submitButton = null;
    this._handleSubmitClickBound = null;

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    if (this._charCountAnnounceTimeout) {
      clearTimeout(this._charCountAnnounceTimeout);
      this._charCountAnnounceTimeout = null;
    }
    if (this._saveStatusClearTimeout) {
      clearTimeout(this._saveStatusClearTimeout);
      this._saveStatusClearTimeout = null;
    }
    if (
      this.hasTextareaTarget &&
      this._noteSaveInFlightCount === 0 &&
      (Boolean(this.saveTimeout) || hasUnsavedEdits)
    ) {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
        this.saveTimeout = null;
      }
      this.performSave();
    }
  }

  debouncedSave() {
    this._noteEditEpoch += 1;
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    this.saveTimeout = setTimeout(() => {
      this.saveTimeout = null;
      this.performSave();
    }, 1000);
  }

  isPersistIdle() {
    return !this.saveTimeout && this._noteSaveInFlightCount === 0;
  }

  reconcileVisibleSubmitButton() {
    this.updateSubmitButtonState(this.isPersistIdle());
  }

  /**
   * Flush debounced/in-flight note saves before unlock/navigation.
   * Typing must not disable feedback submit; submit path awaits this instead.
   * @returns {Promise<boolean>} true when persisted (or nothing to save); false on failure
   */
  flushPendingSave() {
    if (this._flushPromise) {
      return this._flushPromise;
    }

    this._flushPromise = this._runFlushPendingSave().finally(() => {
      this._flushPromise = null;
    });
    return this._flushPromise;
  }

  async _runFlushPendingSave() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    if (!this.hasTextareaTarget) {
      return true;
    }

    try {
      // Drain in-flight PATCH, then persist edits that landed during it.
      // Bound rounds so a missing promise + stale in-flight count cannot spin forever.
      const maxRounds = 10;
      for (let round = 0; round < maxRounds; round += 1) {
        const inFlight = this._activeSavePromise;
        if (inFlight) {
          await inFlight;
          continue;
        }

        if (this._noteSaveInFlightCount > 0) {
          // Count without a promise is inconsistent — treat as idle and continue.
          this._noteSaveInFlightCount = 0;
        }

        if (this._noteEditEpoch > this._noteLastSavedEpoch) {
          await this.performSave({ restoreFocus: false });
          continue;
        }

        return true;
      }

      if (this._noteEditEpoch > this._noteLastSavedEpoch) {
        await this.performSave({ restoreFocus: false });
      }
      return true;
    } catch (error) {
      console.error('Error flushing note save before submit:', error);
      this.showSaveError();
      return false;
    }
  }

  performSave({ restoreFocus = true } = {}) {
    if (!this.hasTextareaTarget) {
      return Promise.resolve();
    }

    const saveStartEpoch = this._noteEditEpoch;
    const noteText = this.textareaTarget.value;
    const selStart = this.textareaTarget.selectionStart;
    const selEnd = this.textareaTarget.selectionEnd;
    const nodeUidForPostSave = this.nodeUidValue;
    const application = this.application;
    const textareaHadFocus = document.activeElement === this.textareaTarget;
    const shouldRestoreFocus = restoreFocus && textareaHadFocus;

    const body = {
      document_user_state: {
        state: {
          [this.nodeUidValue]: {
            value: { value: noteText, updatedAt: Date.now() },
          },
        },
      },
    };

    this._noteSaveInFlightCount += 1;

    let savePromise;
    try {
      savePromise = patchDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: body,
      })
        .then(() => {
          const saveEndEpoch = this._noteEditEpoch;
          const saveSettled = noteSaveEpochsSettled({ saveStartEpoch, saveEndEpoch });
          this._noteLastSavedEpoch = Math.max(this._noteLastSavedEpoch, saveStartEpoch);

          const applyPostSaveToLiveDom = () => {
            const uid = normalizeNodeUid(nodeUidForPostSave);
            const container = document.querySelector(
              `[data-controller~="case-experience--notes"][data-case-experience--notes-node-uid-value="${CSS.escape(uid)}"]`,
            );
            const liveTa = container?.querySelector('[data-case-experience--notes-target="textarea"]');
            if (container && liveTa) {
              if (shouldRestoreFocus) {
                liveTa.focus({ preventScroll: true });
                const len = liveTa.value.length;
                const s = Math.max(0, Math.min(selStart, len));
                const e = Math.max(0, Math.min(selEnd, len));
                try {
                  liveTa.setSelectionRange(s, e);
                } catch (_) {
                  /* invalid range */
                }
              }
              const nextNotes =
                application.getControllerForElementAndIdentifier(
                  container,
                  'case-experience--notes',
                ) ||
                application.getControllerForElementAndIdentifier(container, 'notes');
              if (nextNotes) {
                nextNotes.showSaveIndicatorInContainer(container);
                nextNotes._noteLastSavedEpoch = Math.max(
                  nextNotes._noteLastSavedEpoch,
                  saveStartEpoch,
                );
              } else {
                this.showSaveIndicatorInContainer(container);
                this._noteLastSavedEpoch = Math.max(this._noteLastSavedEpoch, saveStartEpoch);
              }
              liveTa.dispatchEvent(
                new CustomEvent('note:saved', {
                  bubbles: true,
                  detail: { saveStartEpoch, saveEndEpoch },
                }),
              );
              if (nextNotes) {
                nextNotes.updateSubmitButtonState(saveSettled);
              }
            } else if (this.hasTextareaTarget) {
              this.showSaveIndicator();
              this.textareaTarget.dispatchEvent(
                new CustomEvent('note:saved', {
                  bubbles: true,
                  detail: { saveStartEpoch, saveEndEpoch },
                }),
              );
              this.updateSubmitButtonState(saveSettled);
            } else {
              this.updateSubmitButtonState(saveSettled);
            }
          };

          requestAnimationFrame(() => {
            requestAnimationFrame(applyPostSaveToLiveDom);
          });
        })
        .catch((error) => {
          console.error('Error saving note:', error);
          throw error;
        })
        .finally(() => {
          this._noteSaveInFlightCount = Math.max(0, this._noteSaveInFlightCount - 1);
          if (this._activeSavePromise === savePromise) {
            this._activeSavePromise = null;
          }
        });
    } catch (error) {
      this._noteSaveInFlightCount = Math.max(0, this._noteSaveInFlightCount - 1);
      return Promise.reject(error);
    }

    this._activeSavePromise = savePromise;
    // Fire-and-forget callers (debounce/disconnect) must not leave unhandled rejections.
    void savePromise.catch(() => {});
    return savePromise;
  }

  updateSubmitButtonState(saveSettled = true) {
    if (this.fromFeedbackCard) {
      return;
    }
    const button = this.getActiveSubmitButton();
    if (!button) return;

    if (this.gradedValue || this.textareaTarget?.disabled) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    const hasText = notesTrimmedTextAllowsSubmit(this.textareaTarget);
    const answerCommentBlocksSubmit =
      this.hasVisibleAnswerCommentSection() && this.isStudentDisplayMode();
    const enabled = hasText && !answerCommentBlocksSubmit && saveSettled;
    const submitSlice = button.closest('.doc-button-slice');

    setTimeout(() => {
      applyDerivedPassiveSubmitInactive({
        submitSlice,
        application: this.application,
        buttonActionIdentifier: 'case-experience--button-action',
        enabled,
        submitButton: button,
      });
    }, 0);
  }

  handleSubmitClick() {
    // Feedback submit unlock awaits flushPendingSave; do not disable textarea early
    // or a failed save would leave the optional comment field stuck disabled.
    if (this.fromFeedbackCard) {
      return;
    }

    const gradedFeedback = this.element.querySelector('.graded-feedback');
    const hasAnswerComment = hasAnswerCommentAvailable(this.element, this.answerCommentHtml);
    if (gradedFeedback) {
      if (hasAnswerComment) {
        gradedFeedback.innerHTML = this.getGradedFeedbackMessage();
        gradedFeedback.style.display = '';
      } else {
        gradedFeedback.innerHTML = '';
        gradedFeedback.style.display = 'none';
      }
    }

    if (this.hasDisclaimerTarget) {
      this.disclaimerTarget.style.display = 'none';
    }

    if (this.hasTextareaTarget) {
      this.textareaTarget.disabled = true;
    }

    removeSpeechToTextToolbarFromContainer(this.element);
  }

  showSaveIndicator() {
    this.showSaveIndicatorInContainer(this.element);
  }

  showSaveError() {
    this.showSaveErrorInContainer(this.element);
  }

  _resolveSaveIndicatorContainer(container) {
    if (!container) return container;
    const textarea = container.querySelector('[data-case-experience--notes-target="textarea"]');
    if (!textarea) return container;

    return (
      textarea.closest('.doc-text-input-with-stt__field') ||
      textarea.closest('.summary-statement-answer-textarea-wrap') ||
      container
    );
  }

  showSaveIndicatorInContainer(container) {
    if (this._showHelperSaveStatus('Saved', { error: false })) return;

    const target = this._resolveSaveIndicatorContainer(container);
    if (!target) return;
    // Design-system case view loads Tailwind, not TextInput.scss (.save-indicator).
    const indicator = document.createElement('span');
    indicator.className =
      'save-indicator pointer-events-none absolute right-2.5 top-7.5 z-[2] text-xs ' +
      'text-[var(--ds-statuses-success)] opacity-0 transition-opacity duration-300';
    indicator.textContent = 'Saved';
    indicator.setAttribute('aria-live', 'polite');

    const existingIndicator = target.querySelector('.save-indicator, .save-error');
    if (existingIndicator) {
      existingIndicator.remove();
    }

    target.classList.add('relative');
    target.appendChild(indicator);

    setTimeout(() => {
      indicator.classList.add('opacity-100');
    }, 10);

    setTimeout(() => {
      indicator.classList.remove('opacity-100');
      setTimeout(() => indicator.remove(), 300);
    }, 2000);
  }

  showSaveErrorInContainer(container) {
    const message = this._saveErrorMessage();
    if (this._showHelperSaveStatus(message, { error: true })) return;

    const target = this._resolveSaveIndicatorContainer(container);
    if (!target) return;
    const indicator = document.createElement('span');
    indicator.className =
      'save-error pointer-events-none absolute right-2.5 top-7.5 z-[2] text-xs ' +
      'text-[var(--ds-statuses-error)] opacity-0 transition-opacity duration-300';
    indicator.textContent = message;
    indicator.setAttribute('role', 'alert');
    indicator.setAttribute('aria-live', 'assertive');

    const existingIndicator = target.querySelector('.save-indicator, .save-error');
    if (existingIndicator) {
      existingIndicator.remove();
    }

    target.classList.add('relative');
    target.appendChild(indicator);

    setTimeout(() => {
      indicator.classList.add('opacity-100');
    }, 10);
  }

  _saveErrorMessage() {
    if (this.isFreeTextQuestionValue) {
      return 'Answer could not be saved. Try again.';
    }
    return 'Comment could not be saved. Try again.';
  }

  _showHelperSaveStatus(message, { error = false } = {}) {
    if (!this.hasSaveStatusTarget) return false;

    if (this._saveStatusClearTimeout) {
      clearTimeout(this._saveStatusClearTimeout);
      this._saveStatusClearTimeout = null;
    }

    const el = this.saveStatusTarget;
    el.textContent = message;
    el.classList.remove('text-[var(--ds-quiz-results-correct-answer-fg)]', 'text-[var(--ds-statuses-error)]');
    el.classList.add(error ? 'text-[var(--ds-statuses-error)]' : 'text-[var(--ds-quiz-results-correct-answer-fg)]');

    if (error) {
      el.setAttribute('role', 'alert');
    } else {
      el.removeAttribute('role');
      this._saveStatusClearTimeout = setTimeout(() => {
        el.textContent = '';
        el.classList.remove('text-[var(--ds-quiz-results-correct-answer-fg)]', 'text-[var(--ds-statuses-error)]');
        this._saveStatusClearTimeout = null;
      }, 2000);
    }

    return true;
  }
}
